import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Router } from '@lit-labs/router';
import { routes } from './routes';
import { getFirebaseAuth, isDevBypass, isMisconfigured } from './lib/firebase';
import { gateDecision, type AuthStatus } from './lib/auth-gate';
import { currentPath, subscribe, navigate } from './lib/router-state';

import './components/app-topbar';
import './pages/login-page';

/**
 * Root element and the single shell-level authentication gate.
 *
 * Previously each protected route was wrapped in <require-auth>, which
 * was broken: it rendered `<slot>` into the light DOM (slots only
 * project in shadow DOM), so the protected page painted immediately and
 * fired its data fetch before the async auth check ran; the redirect was
 * a soft history.replaceState that never unmounted the child.
 *
 * The gate now lives here, once, and decides what to render BEFORE the
 * router outlet is ever instantiated. On any non-'app' decision the
 * outlet is omitted entirely, so protected pages never construct and
 * never fetch. The decision logic is the pure, unit-tested
 * `gateDecision()` (see lib/auth-gate.ts).
 *
 * We deliberately render into the light DOM (`createRenderRoot`) so the
 * global stylesheet applies to the routed pages without each one having
 * to re-import tokens or duplicate styles.
 */
@customElement('deck-app')
export class DeckApp extends LitElement {
  private router = new Router(this, routes);

  /** Resolved auth status. Starts 'loading' so nothing protected paints. */
  @state() private authStatus: AuthStatus = 'loading';

  /** Current pathname, kept in sync with the router-state singleton. */
  @state() private path = currentPath();

  private unsubscribePath?: () => void;

  // Opt out of shadow DOM so page-level styles in global.css and the
  // deck-system tokens cascade naturally into routed children.
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.path = currentPath();
    this.unsubscribePath = subscribe((p) => {
      this.path = p;
    });
    void this.evaluate();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.unsubscribePath) {
      this.unsubscribePath();
      this.unsubscribePath = undefined;
    }
  }

  protected updated(): void {
    // An already-authed visitor on /login has no sign-in to do — bounce them
    // into the app. Done here (not in render) so render stays side-effect free;
    // navigate() no-ops when already at '/', so there is no redirect loop.
    if (this.authStatus === 'authed' && this.path === '/login') {
      navigate('/');
    }
  }

  /**
   * Resolve the authentication status once at boot.
   *
   *   misconfigured -> 'misconfigured' (fail closed; checked first)
   *   dev-bypass    -> 'authed'        (localhost-only, no config)
   *   otherwise     -> await Firebase; wait for the FIRST onAuthStateChanged
   *                    emission and resolve 'authed' iff a user exists, else
   *                    'anon'. (signInWithCustomToken does a full reload, so
   *                    one-shot resolution here is sufficient — the gate
   *                    re-runs from scratch after sign-in.)
   */
  private async evaluate(): Promise<void> {
    if (isMisconfigured()) {
      this.authStatus = 'misconfigured';
      return;
    }
    if (isDevBypass()) {
      this.authStatus = 'authed';
      return;
    }
    try {
      const auth = await getFirebaseAuth();
      if (!auth) {
        // No auth instance and not dev-bypass/misconfigured shouldn't happen,
        // but fail to login rather than hang on the splash.
        this.authStatus = 'anon';
        return;
      }
      const { onAuthStateChanged } = await import('firebase/auth');
      // Resolve on the first auth-state emission; tear the listener down
      // immediately so it never re-fires (the page reloads on sign-in).
      this.authStatus = await new Promise<AuthStatus>((resolve) => {
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          resolve(user ? 'authed' : 'anon');
        });
      });
    } catch {
      // Firebase failed to load (network blip). Fail to the login route
      // rather than hanging on the splash forever — the user can retry, and
      // no protected content is ever rendered (the outlet stays omitted
      // until status resolves to 'authed').
      this.authStatus = 'anon';
    }
  }

  /** Minimal, chrome-free loading state. No nav-bar, no router outlet. */
  private splash(): TemplateResult {
    return html`
      <div
        role="status"
        aria-live="polite"
        style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--canvas);color:var(--text-secondary);font-family:'Inter',system-ui,sans-serif;font-size:0.95rem;"
      >
        Loading…
      </div>
    `;
  }

  /** Hard, fail-closed error panel for a misconfigured deployment. */
  private blocked(): TemplateResult {
    return html`
      <div
        role="alert"
        style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--canvas);padding:32px;font-family:'Inter',system-ui,sans-serif;"
      >
        <div
          style="max-width:480px;width:100%;background:var(--surface);border:1px solid var(--border-subtle);border-radius:12px;padding:32px;box-shadow:var(--shadow-overlay);"
        >
          <h1 style="font-size:22px;margin:0 0 12px;color:var(--text-primary);">
            Sign-in unavailable
          </h1>
          <p style="margin:0;color:var(--text-secondary);">
            Authentication is not configured for this deployment.
          </p>
        </div>
      </div>
    `;
  }

  render(): TemplateResult {
    const decision = gateDecision(this.authStatus, this.path);

    if (decision === 'splash') return this.splash();
    if (decision === 'blocked') return this.blocked();

    // 'login' (anonymous on a protected route): render the login UI
    // DIRECTLY in the shell — never the router outlet. This is leak-proof
    // by construction: the protected page is never instantiated, so it
    // cannot mount or fire its data fetch, and there is no dependence on
    // @lit-labs/router asynchronously re-resolving to /login (which would
    // briefly show the stale route). After sign-in the login page reloads to
    // '/' and the gate re-evaluates to 'authed'.
    if (decision === 'login') return html`<login-page></login-page>`;

    // 'redirect' — an already-authed visitor on /login. Show the clean,
    // chrome-free splash while updated() bounces them into the app.
    if (decision === 'redirect') return this.splash();

    // decision === 'app' — render the outlet. Show the nav-bar on every
    // surface except anonymous, chrome-owning views: share/live, and the
    // /accept invite landing (the invitee is not signed in and the page owns
    // its own centred chrome, same as login).
    const showNav =
      !this.path.startsWith('/share/') &&
      !this.path.startsWith('/live/') &&
      this.path !== '/accept';
    return html`
      <div class="app-shell">
        ${showNav ? html`<app-topbar></app-topbar>` : ''}
        <main class="app-main">${this.router.outlet()}</main>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'deck-app': DeckApp;
  }
}
